/**
 * ENGLISH — the master dictionary and the fallback for every other language.
 *
 * This file defines the `Dict` shape, so a new language file that forgets a key
 * fails `npm run typecheck` instead of silently showing a raw key on screen.
 * Keep the nesting shallow and the names boring: `page.block.item`.
 */
export const en = {
  nav: {
    home: 'Home',
    collection: 'Collection',
    maison: 'The Maison',
    contact: 'Contact',
    studio: 'Studio',
    signIn: 'Sign in',
    signOut: 'Sign out',
    menu: 'Menu',
    close: 'Close',
    openMenu: 'Open menu',
  },

  common: {
    viewAll: 'View all',
    loading: 'Loading…',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    search: 'Search',
    required: 'Required',
    optional: 'Optional',
    all: 'All',
    from: 'From',
    reset: 'Reset',
    apply: 'Apply',
    filters: 'Filters',
    copied: 'Copied',
    of: 'of',
  },

  theme: {
    label: 'Appearance',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
  },

  lang: {
    label: 'Language',
  },

  movement: {
    label: 'Movement',
    automatic: 'Automatic',
    manual: 'Manual winding',
    quartz: 'Quartz',
  },

  condition: {
    label: 'Condition',
    new: 'New',
    pre_owned: 'Pre-owned',
    vintage: 'Vintage',
  },

  status: {
    published: 'Published',
    draft: 'Draft',
    reserved: 'Reserved',
    sold: 'Sold',
  },

  home: {
    heroEyebrow: 'Since 1948 · Geneva',
    heroTitle: 'Time, kept beautifully.',
    heroLead:
      'A small, deliberately short list of mechanical watches — each one inspected, timed and guaranteed by our own watchmakers.',
    heroCta: 'Explore the collection',
    heroSecondary: 'Our story',
    searchTitle: 'Find your watch',
    searchLead: '{count} pieces in the salon — every one opened, timed and guaranteed by our watchmakers.',
    quickAll: 'All pieces',
    gridTitle: 'In the salon now',
    gridRefine: 'Filter and refine',
    marqueeService: 'Lifetime servicing',
    marqueeAuthenticity: 'Certified authenticity',
    marqueeShipping: 'Insured worldwide delivery',
    marqueeReturns: '14-day return',
    collectionsEyebrow: 'Collections',
    collectionsTitle: 'Four families, one standard',
    collectionsSubtitle:
      'Dive, dress, chronograph and heritage. Every piece is held to the same measure: a movement worth keeping for fifty years.',
    featuredEyebrow: 'Selected pieces',
    featuredTitle: 'This season at the boutique',
    featuredSubtitle: 'Hand-picked by our head watchmaker and available to view in Hanoi or Geneva.',
    storyEyebrow: 'The atelier',
    storyTitle: 'Seventy-six years at one bench',
    storyBody:
      'We are watchmakers before we are merchants. Every watch that enters the boutique is opened, timed on six positions and regulated by hand before it is listed — and it goes home with a service book signed by the person who did the work.',
    storyCta: 'Inside the maison',
    serviceEyebrow: 'The promise',
    serviceTitle: 'What comes with the watch',
    service1Title: 'Certified authenticity',
    service1Body: 'Every reference is verified against the manufacture archive and comes with a written certificate.',
    service2Title: 'Lifetime servicing',
    service2Body: 'Free regulation for the first three years, and a bench that will still know your watch in 2050.',
    service3Title: 'Insured delivery',
    service3Body: 'Fully insured worldwide shipping, hand-delivered within Hanoi and Ho Chi Minh City.',
    visitEyebrow: 'Visit',
    visitTitle: 'Sit down with the watch before you decide',
    visitBody: 'Private viewings, by appointment, at both salons. Tea, loupe, and no obligation whatsoever.',
    visitCta: 'Request a viewing',
  },

  search: {
    label: 'Search',
    placeholder: 'Search a name, reference or maison…',
    openHint: 'Search the collection',
    recent: 'Recent searches',
    clearRecent: 'Clear',
    seeAll: 'See all {count} results',
    noResults: 'Nothing matched “{q}”',
    noResultsBody: 'Try fewer words, or a reference like TA-300.24.',
    browseAll: 'Browse the whole collection',
    resultsFor: 'Results for “{q}”',
  },

  catalog: {
    eyebrow: 'The collection',
    title: 'Every piece in the salon',
    subtitle: 'Filter by family, movement or budget. Prices shown are final, tax and servicing included.',
    searchPlaceholder: 'Search a reference or a name…',
    collection: 'Collection',
    maison: 'Maison',
    price: 'Price',
    sort: 'Sort',
    sortRelevance: 'Best match',
    sortNewest: 'Latest arrivals',
    sortPriceAsc: 'Price, low to high',
    sortPriceDesc: 'Price, high to low',
    sortFeatured: 'Featured first',
    priceAny: 'Any price',
    priceUnder5k: 'Under 5,000',
    price5to10k: '5,000 – 10,000',
    price10to20k: '10,000 – 20,000',
    priceOver20k: 'Above 20,000',
    count: '{count} pieces',
    countOne: '1 piece',
    emptyTitle: 'Nothing matches those filters',
    emptyBody: 'Widen the price range or clear a filter — new pieces arrive every week.',
    clearFilters: 'Clear filters',
  },

  watch: {
    reference: 'Reference',
    caseMaterial: 'Case',
    caseSize: 'Diameter',
    water: 'Water resistance',
    strap: 'Strap',
    year: 'Year',
    specs: 'Specification',
    description: 'About this piece',
    enquire: 'Enquire about this watch',
    availability: 'Availability',
    inStock: 'In the salon',
    lastOne: 'Last one available',
    outOfStock: 'Currently reserved',
    related: 'You may also like',
    backToCollection: 'Back to the collection',
    saved: 'Saved',
    share: 'Share',
    linkCopied: 'Link copied to your clipboard',
    notFoundTitle: 'This piece is no longer listed',
    notFoundBody: 'It may have found an owner. The rest of the collection is waiting.',
  },

  enquiry: {
    title: 'Ask about this watch',
    subtitle: 'Tell us how to reach you and we will answer the same day.',
    name: 'Your name',
    email: 'Email',
    phone: 'Phone',
    message: 'Message',
    messagePlaceholder: 'I would like to see this piece in Hanoi this weekend…',
    submit: 'Send enquiry',
    success: 'Thank you — we will be in touch within the day.',
    errorName: 'Please tell us your name',
    errorEmail: 'That email address does not look right',
    errorMessage: 'Please add a short message',
  },

  maison: {
    eyebrow: 'The Maison',
    title: 'A boutique built around a bench',
    lead:
      'TEMPORA began in 1948 as a repair workshop on the ground floor of a Geneva apartment building. Three generations later we still do the same thing: open the case, understand the movement, and only then decide whether a watch deserves to be sold.',
    chapter1Title: 'We buy the movement, not the name',
    chapter1Body:
      'A dial can be repainted and a box can be printed. A movement cannot lie. Every piece is dismantled, inspected under the loupe and timed on six positions for 72 hours before it is priced.',
    chapter2Title: 'One list, honestly kept',
    chapter2Body:
      'We would rather show you twelve watches we can vouch for than four hundred we cannot. If a piece is not here, it did not pass — and we will happily tell you why.',
    chapter3Title: 'The watch outlives the sale',
    chapter3Body:
      'Servicing is free for three years and available for as long as we exist. Bring a watch back in twenty years and the same bench, with the same tools, will take care of it.',
    statYears: 'Years at the bench',
    statPieces: 'Pieces restored',
    statCountries: 'Countries served',
    statService: 'Service guarantee',
    ctaTitle: 'Come and look through the loupe',
    ctaBody: 'Both salons keep a quiet room for viewings. No appointment is ever rushed.',
    ctaButton: 'Book a viewing',
  },

  contact: {
    eyebrow: 'Contact',
    title: 'Write, call, or simply come by',
    subtitle: 'We answer every message the same working day.',
    salons: 'Salons',
    hours: 'Opening hours',
    hoursWeekday: 'Tuesday – Saturday, 10:00 – 19:00',
    hoursSunday: 'Sunday & Monday, by appointment',
    phone: 'Telephone',
    email: 'Email',
    formTitle: 'Send a message',
    formSubject: 'Subject',
    subjectViewing: 'Book a viewing',
    subjectPiece: 'A piece in the collection',
    subjectService: 'Servicing my watch',
    subjectOther: 'Something else',
  },

  auth: {
    signInTitle: 'Boutique account',
    signInSubtitle: 'The studio is reserved for the owner of the boutique.',
    signUpTitle: 'Create an account',
    signUpSubtitle: 'Save the pieces you like and follow their availability.',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 6 characters',
    signIn: 'Sign in',
    signUp: 'Create account',
    toSignUp: 'No account yet? Create one',
    toSignIn: 'Already have an account? Sign in',
    ownerHintTitle: 'Demo access',
    ownerHintBody:
      'No database is configured yet, so sign-in is simulated. Use {email} with any password of 6+ characters to enter the owner studio.',
    signedIn: 'Signed in',
    signedOut: 'Signed out',
    errorEmail: 'Enter a valid email address',
    errorPassword: 'Password must be at least 6 characters',
  },

  guard: {
    title: 'Owner access only',
    body: 'The studio — posting and managing listings — belongs to the boutique owner account.',
    button: 'Sign in as the owner',
    wrongAccountTitle: 'This account cannot open the studio',
    wrongAccountBody: 'You are signed in as {email}. Sign out and use the boutique owner account.',
  },

  studio: {
    eyebrow: 'Owner studio',
    overview: 'Overview',
    listings: 'Listings',
    newListing: 'Post a listing',
    enquiries: 'Enquiries',
    welcome: 'Good to see you, {name}',
    welcomeBody: 'Everything the boutique shows the public is managed from here.',
    statPublished: 'Published',
    statDraft: 'Drafts',
    statReserved: 'Reserved & sold',
    statValue: 'Catalogue value',
    statEnquiries: 'New enquiries',
    recentEnquiries: 'Latest enquiries',
    recentListings: 'Recently posted',
    viewPublic: 'View in the boutique',
    manageTitle: 'Manage listings',
    manageSubtitle: 'Publish, hide, price and retire every piece in the salon.',
    searchListings: 'Search your listings…',
    filterStatus: 'Status',
    tableWatch: 'Piece',
    tablePrice: 'Price',
    tableStatus: 'Status',
    tableViews: 'Views',
    tableDate: 'Posted',
    tableActions: 'Actions',
    emptyTitle: 'No listing yet',
    emptyBody: 'Post the first piece and it appears in the boutique straight away.',
    deleteTitle: 'Delete this listing?',
    deleteBody: '“{title}” will be removed from the boutique. This cannot be undone.',
    deleted: 'Listing deleted',
    statusChanged: 'Status updated',
    formNewTitle: 'Post a piece',
    formEditTitle: 'Edit the listing',
    formSubtitle: 'English is the base text; the Vietnamese fields are optional and fall back to English.',
    fieldTitle: 'Name of the piece',
    fieldTitleVi: 'Name in Vietnamese',
    fieldReference: 'Reference',
    fieldCollection: 'Collection',
    fieldMaison: 'Maison',
    fieldPrice: 'Price',
    fieldOldPrice: 'Previous price',
    fieldOldPriceHint: 'Leave at 0 for no strike-through',
    fieldMovement: 'Movement',
    fieldCase: 'Case material',
    fieldSize: 'Diameter',
    fieldWater: 'Water resistance',
    fieldStrap: 'Strap',
    fieldYear: 'Year',
    fieldCondition: 'Condition',
    fieldStock: 'Pieces available',
    fieldStatus: 'Status',
    fieldFeatured: 'Show on the home page',
    fieldImage: 'Main image URL',
    fieldGallery: 'Gallery image URLs',
    fieldGalleryHint: 'One URL per line',
    fieldDescription: 'Description',
    fieldDescriptionVi: 'Description in Vietnamese',
    sectionIdentity: 'Identity',
    sectionSpecs: 'Specification',
    addSpec: 'Add a row',
    sectionMedia: 'Images',
    sectionCopy: 'Copy',
    sectionPublishing: 'Publishing',
    saveDraft: 'Save as draft',
    publish: 'Publish to the boutique',
    updated: 'Listing updated',
    created: 'Listing published',
    errorTitle: 'The piece needs a name',
    errorPrice: 'Enter a price above zero',
    errorImage: 'A main image is required',
    inboxTitle: 'Enquiries',
    inboxSubtitle: 'Everyone who asked about a piece, newest first.',
    inboxEmpty: 'No enquiry yet',
    inboxEmptyBody: 'Messages sent from a watch page land here.',
    markAnswered: 'Mark as answered',
    markArchived: 'Archive',
    enquiryNew: 'New',
    enquiryAnswered: 'Answered',
    enquiryArchived: 'Archived',
    aboutPiece: 'About',
  },

  notFound: {
    title: 'This page has gone quiet',
    body: 'The link may be old, or the piece may have found its owner.',
    button: 'Back to the boutique',
  },

  footer: {
    tagline: 'Fine mechanical watches, chosen and serviced by our own watchmakers.',
    explore: 'Explore',
    house: 'The house',
    legal: 'Terms & privacy',
    newsletterTitle: 'Quiet letters, four times a year',
    newsletterBody: 'New arrivals and notes from the bench. No noise.',
    newsletterPlaceholder: 'Your email address',
    newsletterCta: 'Subscribe',
    newsletterDone: 'Thank you — welcome aboard.',
    rights: 'All rights reserved.',
    mockNotice: 'Demo data — configure DATABASE_URL to connect your database.',
  },
}

/** The shape every other language file must satisfy. */
export type Dict = typeof en
